import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n, useLocalizedPath } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { EntityCard } from '@/components/EntityCard';
import { OQueFazerSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { OQueFazerQuiz } from '@/components/OQueFazerQuiz';
import taxonomia from '@/data/passeios-taxonomia.json';

type TaxItem = {
  id: string;
  titulo: string;
  icon: string;
  blurb: string;
  priceFrom: number | null;
  priceNote?: string | null;
  duration?: string | null;
  difficulty: string | null;
  profile: string[];
  reservavel?: boolean;
  link: string | null;
};
type Subtipo = { key: string; icon: string; titulo: string; lead: string; itens: TaxItem[] };

const SUBTIPOS = taxonomia.subtipos as Subtipo[];
const ALL_ITENS: (TaxItem & { subKey: string; subTitulo: string })[] = SUBTIPOS.flatMap(s =>
  s.itens.map(it => ({ ...it, subKey: s.key, subTitulo: s.titulo }))
);
const precoLabel = (it: TaxItem) => (it.priceFrom ? `a partir de R$${it.priceFrom}` : 'Sob consulta');

function PasseioCard({ it }: { it: TaxItem }) {
  const inner = (
    <div className="h-full bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-teal-light/40 transition-all">
      <div className="flex items-start gap-3 mb-2">
        <span className="text-3xl flex-shrink-0" aria-hidden="true">{it.icon}</span>
        <h3 className="font-display text-lg text-warm-dark leading-tight">{it.titulo}</h3>
      </div>
      <p className="font-body text-sm text-warm-gray leading-relaxed mb-3">{it.blurb}</p>
      <div className="flex flex-wrap gap-1.5">
        {it.difficulty && (
          <span className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">🥾 {it.difficulty}</span>
        )}
        <span className="font-body text-[11px] bg-teal-light/10 text-teal-deep px-2 py-0.5 rounded-full">💰 {precoLabel(it)}</span>
        {it.link && <span className="font-body text-[11px] text-teal-deep font-semibold ml-auto self-center">Ver →</span>}
      </div>
    </div>
  );
  return it.link ? (
    <Link to={it.link} className="block">{inner}</Link>
  ) : (
    <div>{inner}</div>
  );
}

export function OQueFazerPage() {
  const { lang } = useI18n();
  const localize = useLocalizedPath();
  const { t } = useTranslation();
  const atracoesList = getPublished('atracao', lang);
  const trilhasList = getPublished('trilha', lang);
  const faq = (taxonomia.faq || []) as { q: string; a: string }[];

  return (
    <>
      <OQueFazerSEO />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-56 sm:h-64">
            <OptimizedImage
              src="/images/farol-card.jpg"
              alt={t('thingsToDo.heroImageAlt')}
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 hero-gradient" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-5xl sm:text-6xl text-white mb-2">
                O que fazer na Ilha do Mel
              </h1>
              <p className="font-editorial text-xl text-white/80">
                Passeios de barco, trilhas, esportes e experiências — o guia pra montar seus dias na ilha
              </p>
            </div>
          </div>
        </div>

        <Container>
          {/* Intro */}
          <div className="max-w-3xl mx-auto mb-12 space-y-4">
            <p className="font-editorial text-lg text-warm-gray leading-relaxed">
              A <Link to="/" className="text-teal-deep font-medium hover:underline">Ilha do Mel</Link> não tem carro,
              shopping nem agito de cidade — o que ela tem é o que fazer ao ar livre, e muito. De um lado, a natureza
              bruta (praias, trilhas na Mata Atlântica, o Farol, a Gruta); do outro, os passeios: barco pela Baía dos
              Golfinhos, esportes na água, voo livre e experiências pra desacelerar.
            </p>
            <p className="font-body text-warm-gray leading-relaxed">
              Pra não te afogar em opções, organizei tudo por tipo — <strong>náuticos, esportes aquáticos, trilhas,
              pontos turísticos e experiências</strong> — com duração, faixa de preço e nível de esforço. Se bater a
              dúvida, faz o teste de 3 perguntas aqui embaixo que eu indico por onde começar.
            </p>
          </div>

          {/* Quiz */}
          <OQueFazerQuiz taxonomia={taxonomia} />

          {/* Tabela comparativa */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">Passeios lado a lado</h2>
            <p className="font-editorial text-warm-gray mb-6">
              Duração, faixa de preço e nível de esforço de cada atividade — pra comparar de um golpe de vista.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-sand-200">
              <table className="w-full text-left border-collapse min-w-[640px]">
                <thead>
                  <tr className="bg-sand-50 text-warm-dark">
                    <th className="font-display text-sm p-3">Atividade</th>
                    <th className="font-display text-sm p-3">Tipo</th>
                    <th className="font-display text-sm p-3">Duração</th>
                    <th className="font-display text-sm p-3">A partir de</th>
                    <th className="font-display text-sm p-3">Nível</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_ITENS.map((it, i) => (
                    <tr key={it.id} className={`border-t border-sand-100 ${i % 2 ? 'bg-sand-50/40' : 'bg-white'}`}>
                      <td className="p-3 font-body text-sm text-warm-dark font-medium">
                        <span className="mr-1.5" aria-hidden="true">{it.icon}</span>
                        {it.link ? <Link to={it.link} className="hover:text-teal-deep hover:underline">{it.titulo}</Link> : it.titulo}
                      </td>
                      <td className="p-3 font-body text-sm text-warm-gray">{it.subTitulo}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{it.duration || '—'}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{it.priceFrom ? `R$${it.priceFrom}` : 'Sob consulta'}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{it.difficulty || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-body text-xs text-warm-gray mt-2">{taxonomia.priceDisclaimer}</p>
          </section>

          {/* Blocos por subtipo (náuticos / esportes / experiências) */}
          {SUBTIPOS.map(sub => (
            <section key={sub.key} className="mb-16" id={`subtipo-${sub.key}`}>
              <h2 className="flex items-center gap-2 font-display text-3xl sm:text-4xl text-warm-dark mb-2">
                <span aria-hidden="true">{sub.icon}</span> {sub.titulo}
              </h2>
              <p className="font-editorial text-warm-gray mb-6 max-w-3xl">{sub.lead}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sub.itens.map(it => <PasseioCard key={it.id} it={it} />)}
              </div>
            </section>
          ))}

          {/* Trilhas (dados live) */}
          {trilhasList.length > 0 && (
            <section className="mb-16" id="subtipo-trilha">
              <h2 className="flex items-center gap-2 font-display text-3xl sm:text-4xl text-warm-dark mb-2">
                <span aria-hidden="true">🥾</span> Trilhas
              </h2>
              <p className="font-editorial text-warm-gray mb-6 max-w-3xl">
                Sem carros, as trilhas são o jeito de cruzar a ilha a pé pela Mata Atlântica — do Farol à Fortaleza,
                da Gruta ao Istmo. Compare as 6 rotas no{' '}
                <Link to={localize('/trilhas-ilha-do-mel')} className="text-teal-deep font-medium hover:underline">
                  guia de trilhas na Ilha do Mel
                </Link>{' '}
                ou veja os destaques abaixo.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {trilhasList.map((entity: any) => (
                  <EntityCard key={entity.handle} entity={entity} category="trilhas" />
                ))}
              </div>
            </section>
          )}

          {/* Pontos turísticos (dados live) — linka pro pilar */}
          {atracoesList.length > 0 && (
            <section className="mb-16" id="subtipo-atracao">
              <h2 className="flex items-center gap-2 font-display text-3xl sm:text-4xl text-warm-dark mb-2">
                <span aria-hidden="true">📍</span> Pontos turísticos
              </h2>
              <p className="font-editorial text-warm-gray mb-6 max-w-3xl">
                O Farol das Conchas, a Gruta das Encantadas, a Fortaleza e a Baía dos Golfinhos são parada obrigatória.
                Veja os principais abaixo ou explore o{' '}
                <Link to={localize('/pontos-turisticos-ilha-do-mel')} className="text-teal-deep font-medium hover:underline">
                  guia dos pontos turísticos na Ilha do Mel
                </Link>.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {atracoesList.map((entity: any) => (
                  <EntityCard key={entity.handle} entity={entity} category="atracoes" />
                ))}
              </div>
            </section>
          )}

          {/* Passeio de barco em destaque */}
          <section className="mb-16 bg-teal-light/5 border border-teal-light/20 rounded-2xl p-6 sm:p-8" id="barco-destaque">
            <span className="font-body text-sm text-teal-deep font-semibold uppercase tracking-wider">O mais pedido</span>
            <h2 className="font-display text-3xl text-warm-dark mt-1 mb-3">🚤 Passeio de barco: a ilha vista do mar</h2>
            <p className="font-body text-warm-gray leading-relaxed mb-4 max-w-3xl">
              Se é pra fazer um passeio só, que seja o de barco. A volta completa (4 a 5 horas) contorna a ilha, passa
              pelos costões, pela Ponta Oeste e pela Baía dos Golfinhos — onde vivem os botos-cinza (avistar não é
              garantido, mas quando rola é o momento da viagem). Sai a partir de <strong>R$150 por pessoa</strong> no
              compartilhado; dá pra fechar na hora nos trapiches ou reservar antes na alta temporada.
            </p>
            <Link
              to={localize('/passeios-de-barco-ilha-do-mel')}
              className="inline-flex items-center gap-2 bg-teal-deep hover:bg-teal-deep/90 text-white font-body font-semibold rounded-xl px-6 py-3 transition-all"
            >
              Ver rotas, preços e como reservar →
            </Link>
          </section>

          {/* Logística que conecta */}
          <section className="mb-16" id="logistica">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Antes de escolher, o básico</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: '⛴️', t: 'Como chegar', d: 'Barca por Pontal do Sul (~R$45 ida e volta) ou Paranaguá. Sem carros na ilha: a pé, de bike ou táxi náutico entre as vilas.', to: '/como-chegar' },
                { icon: '🗓️', t: 'Melhor época', d: 'Verão (dez–fev) pra golfinhos e mar; out–mar concentra os melhores dias. Inverno é instável, mar mais agitado.' },
                { icon: '🌊', t: 'A maré manda', d: 'Gruta das Encantadas e Praia de Fora só na maré baixa (~8h–13h); o barco pode não fechar a volta se o mar estiver bravo.' },
                { icon: '🌧️', t: 'E se chover?', d: 'Cafés, artesanato e gastronomia nas vilas; com chuva fraca, bike até a Fortaleza ou à Gruta. Trilhas ficam escorregadias.' },
              ].map(c => (
                <div key={c.t} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-sand-200">
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">{c.icon}</span>
                  <div>
                    <h3 className="font-display text-lg text-warm-dark mb-1">
                      {c.to ? <Link to={localize(c.to)} className="hover:text-teal-deep hover:underline">{c.t}</Link> : c.t}
                    </h3>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          {faq.length > 0 && (
            <section className="mb-16" id="faq">
              <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Perguntas frequentes</h2>
              <div className="space-y-3 max-w-3xl">
                {faq.map((f, i) => (
                  <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                      <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
                      <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
                    </summary>
                    <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.a}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* CTA + links internos */}
          <section className="mb-16 bg-teal-deep rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-white mb-3">Monte o resto da viagem</h2>
            <p className="font-editorial text-white/80 mb-6 max-w-xl mx-auto">
              Já sabe o que fazer? Agora resolva onde ficar, onde comer e quais praias priorizar.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to={localize('/praias-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Praias</Link>
              <Link to={localize('/pousadas-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Pousadas</Link>
              <Link to={localize('/restaurantes-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Restaurantes</Link>
            </div>
          </section>
        </Container>
      </div>
    </>
  );
}
